import React, { useState } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Section from './components/Section';
import ProjectCard from './components/ProjectCard';
import Timeline from './components/Timeline';
import PatentList from './components/PatentList';
import Footer from './components/Footer';
import ImageCarousel from './components/ImageCarousel';
import { PROJECTS, SECONDARY_PROJECTS, MEDIA, SHORT_BIO, LONG_BIO, SOCIAL_LINKS } from './constants';
import { ArrowUpRight, Check, Copy, Heart, Zap, Quote } from 'lucide-react';

// --- Organic SVG Section Dividers ---

type DividerStyle = 'curve' | 'wave' | 'drip' | 'clouds' | 'swoop' | 'slope' | 'hill';

interface SectionDividerProps {
  position: 'top' | 'bottom';
  fill: string;
  style: DividerStyle;
  height?: string;
  flip?: boolean;
  invert?: boolean; // 180deg rotation
}

const SectionDivider: React.FC<SectionDividerProps> = ({ position, fill, style, height = "120px", flip = false, invert = false }) => {
  const wrapperClass = position === 'top' 
    ? "absolute top-0 left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none translate-y-[-1px]" 
    : "absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none translate-y-[1px]";

  const transforms = [];
  if (flip) transforms.push('scaleX(-1)');
  if (invert) transforms.push('rotate(180deg)');
  const transform = transforms.join(' ');

  const renderPath = () => {
    switch (style) {
      case 'curve':
        // Gentle convex curve
        return <path d="M0,0 L1440,0 L1440,80 C1100,160 340,160 0,80 Z" fill={fill} />;
      case 'wave':
        // Smooth sine-like wave
        return <path d="M0,0 L1440,0 L1440,50 C1100,120 900,20 720,70 C500,120 300,20 0,60 Z" fill={fill} />;
      case 'drip':
        // Deep organic drip/pointer
        return <path d="M0,0 L1440,0 L1440,50 Q720,180 0,50 Z" fill={fill} />;
      case 'clouds':
        // Multiple bubbly curves
        return <path d="M0,0 L1440,0 L1440,60 Q1296,120 1152,60 Q1008,0 864,60 Q720,120 576,60 Q432,0 288,60 Q144,120 0,60 Z" fill={fill} />;
      case 'swoop':
        // Asymmetric smooth curve
        return <path d="M0,0 L1440,0 L1440,100 C900,180 300,30 0,120 Z" fill={fill} />;
      case 'slope':
        // Gentle organic slope
        return <path d="M0,0 L1440,0 L1440,100 C960,100 480,150 0,50 Z" fill={fill} />;
      case 'hill':
         // Sharper hill
         return <path d="M0,0 L1440,0 L1440,40 C1000,40 720,150 440,40 C200,40 0,100 0,100 Z" fill={fill} />;
      default:
        return null;
    }
  };

  const viewBox = "0 0 1440 180";

  return (
    <div className={wrapperClass}>
      <svg 
        className="relative block w-full" 
        style={{ height, transform }}
        data-name="Layer 1" 
        xmlns="http://www.w3.org/2000/svg" 
        viewBox={viewBox} 
        preserveAspectRatio="none"
      >
        {renderPath()}
      </svg>
    </div>
  );
};


const App: React.FC = () => {
  const [activeBioTab, setActiveBioTab] = useState<'short' | 'long'>('short');
  const [copied, setCopied] = useState(false);

  const handleCopyBio = () => {
    const text = activeBioTab === 'short' ? SHORT_BIO : LONG_BIO;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white animate-in fade-in duration-700 overflow-x-hidden">
      <Navigation />
      <main>
      
      {/* 1. HERO SECTION (White) */}
      <div id="top" className="relative bg-white z-0 pb-20">
        <Hero />
      </div>

      {/* 2. FEATURED WORK (Soft Pink) */}
      <div className="relative bg-[#FFF5F7] pb-48 md:pb-64">
        {/* Top: White Curve */}
        <SectionDivider position="top" fill="#ffffff" style="curve" height="100px" />
        
        <Section 
          id="work" 
          title="Featured Work" 
          subtitle="Highlights from my recent work at Google Labs, focusing on creative tools and AI applications."
          className="bg-transparent !pt-48"
        >
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {PROJECTS.map((project, idx) => (
              <div key={idx} className="h-full">
                <ProjectCard project={project} large />
              </div>
            ))}
          </div>
        </Section>

        {/* Bottom: Cream Slope rising up */}
        <SectionDivider position="bottom" fill="#FFF9F0" style="slope" height="150px" invert />
      </div>

      {/* 3. WHERE IT STARTED (Cream) */}
      <div className="relative bg-[#FFF9F0] pb-32">
        <Section 
          title="Where It Started" 
          subtitle="Foundational platforms and initiatives that paved the way." 
          className="bg-transparent !pt-32"
        >
          <div className="grid md:grid-cols-2 gap-8">
            {SECONDARY_PROJECTS.map((project, idx) => (
              <ProjectCard key={idx} project={project} />
            ))}
          </div>
        </Section>
        
        {/* Bottom: Dark Drip coming down from Speaking is handled by Speaking Top? 
            No, let's put it here as 'fill dark'. 
            If I put a divider at bottom here with fill Dark, it looks like Dark section rising up.
            Let's use 'Drip' style inverted? No 'Drip' is usually top-down.
            Let's let Speaking section handle the top divider.
        */}
      </div>

      {/* 4. SPEAKING & EVENTS (Dark) */}
      <div className="relative bg-[#1a1a1a] pb-32">
        {/* Top: White/Cream Drip pointing DOWN into Dark */}
        {/* Since previous section is #FFF9F0, the fill must be #FFF9F0 */}
        <SectionDivider position="top" fill="#FFF9F0" style="drip" height="120px" />
        
        <Section 
          id="speaking" 
          title="Speaking & Events" 
          subtitle="Keynotes, interviews, and conversations about the future of product and AI."
          className="bg-transparent !pt-32"
          titleColor="text-white"
          subtitleColor="text-gray-400"
        >
          <div className="-mx-6 md:-mx-12 mb-10">
            <ImageCarousel />
          </div>

          <div className="max-w-4xl mx-auto mb-12 text-center">
            <a
              href={SOCIAL_LINKS.talks}
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-gray-600 text-white text-sm font-bold uppercase tracking-wider hover:bg-[#FF5E7E] hover:border-[#FF5E7E] transition-colors"
            >
              See all talks &amp; slide decks <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>

          <div className="max-w-4xl mx-auto grid gap-6">
             {MEDIA.map((item, idx) => (
               <a 
                 key={idx} 
                 href={item.links[0]?.url}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="group block bg-[#252525] hover:bg-[#2a2a2a] border border-gray-800 p-8 rounded-[2rem] transition-all duration-300 hover:scale-[1.01]"
               >
                 <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[10px] font-bold text-[#FF5E7E] uppercase tracking-widest bg-[#FF5E7E]/10 px-3 py-1 rounded-full">{item.type}</span>
                        <span className="text-sm text-gray-500 font-serif italic">{item.source}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white group-hover:text-[#FF5E7E] transition-colors">{item.title}</h3>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500 group-hover:text-white transition-colors text-sm font-bold uppercase tracking-wider">
                      {(item.type === 'Video' || item.type === 'Keynote' || item.links[0]?.platform === 'YouTube') ? 'Watch' : (item.type === 'Article' || item.type === 'Interview') ? 'Read' : 'Listen'} <ArrowUpRight size={18} />
                    </div>
                 </div>
               </a>
             ))}
          </div>
        </Section>

        {/* Bottom: Lavender Blush Wave rising UP */}
        <SectionDivider position="bottom" fill="#FFF0F3" style="wave" height="120px" invert />
      </div>

      {/* 5. EXPERIENCE (Lavender Blush) */}
      <div className="relative bg-[#FFF0F3] pb-32">
        <Section 
          id="experience" 
          title="Experience Timeline" 
          className="bg-transparent !pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <Timeline />
          </div>
        </Section>
        
        {/* Bottom: Seashell Clouds rising UP */}
        <SectionDivider position="bottom" fill="#FFF5EE" style="clouds" height="140px" invert />
      </div>

      {/* 6. PATENTS (Seashell) */}
      <div className="relative bg-[#FFF5EE] pb-48">
        <Section 
          id="patents" 
          title="Patents & Innovation" 
          subtitle="Contributions to intellectual property in AI, voice assistants, and UI."
          className="bg-transparent !pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <PatentList />
          </div>
        </Section>

        {/* Bottom: Beige Swoop rising UP */}
        <SectionDivider position="bottom" fill="#FDFBF7" style="swoop" height="140px" invert />
      </div>

      {/* 7. BIO (Beige/Cream) */}
      <div className="relative bg-[#FDFBF7] pb-24">
        <Section id="bio" title="Bio" className="bg-transparent !pt-32">
          <div className="max-w-3xl mx-auto">
            {/* Header with Tabs and Copy Button */}
            <div className="flex items-end justify-between border-b border-gray-200 mb-8">
              <div className="flex gap-8">
                <button 
                  onClick={() => setActiveBioTab('short')}
                  className={`pb-4 text-sm font-bold uppercase tracking-widest transition-colors ${activeBioTab === 'short' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Short Bio
                </button>
                <button 
                  onClick={() => setActiveBioTab('long')}
                  className={`pb-4 text-sm font-bold uppercase tracking-widest transition-colors ${activeBioTab === 'long' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Long Bio
                </button>
              </div>

              <button 
                onClick={handleCopyBio}
                className={`pb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-all hover:text-[#FF5E7E] ${
                  copied ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                 {copied ? <Check size={16} /> : <Copy size={16} />}
                 <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
            
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-[#EFEBE5]">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-light whitespace-pre-wrap">
                {activeBioTab === 'short' ? SHORT_BIO : LONG_BIO}
              </p>
            </div>
          </div>
        </Section>
        
        {/* Bottom: Personal Section Gradient Start Color (Misty Rose/Peach) */}
        {/* Use Hill shape */}
        <SectionDivider position="bottom" fill="#FFF5F5" style="hill" height="150px" invert />
      </div>

      {/* 8. PERSONAL (Peach/Pink Gradient) */}
      <div className="relative bg-gradient-to-b from-[#FFF5F5] to-[#FFEBE5] pb-32">
         {/* Top divider handled by Bio bottom */}
         
         <Section id="personal" className="bg-transparent !pt-40 md:!pt-48 !pb-0">
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-start mb-20">
              
              {/* Beyond the Office Card */}
              <div className="relative group">
                <div className="absolute -top-3 -left-3 bg-[#FFEAEA] p-2 rounded-full z-10 shadow-sm border border-white">
                  <Heart size={20} className="text-[#FF5E7E]" fill="currentColor" />
                </div>
                <div className="bg-white p-10 md:p-14 rounded-[2.5rem] border border-[#FFEAEA] shadow-sm transform group-hover:-translate-y-2 transition-transform duration-500">
                  <h3 className="font-serif text-3xl font-bold mb-6 text-gray-900">Beyond the Office</h3>
                  <p className="text-2xl font-serif italic text-gray-700 leading-relaxed mb-6">
                    "When I'm not in the office, I live in San Francisco with my husband, our three young kids, a dog, and a cat."
                  </p>
                </div>
              </div>

              {/* Side Projects & Interests Section */}
              <div className="pt-4">
                <div className="flex items-center gap-3 mb-6">
                  <Zap size={24} className="text-[#FF7235]" fill="currentColor" />
                  <h3 className="font-serif text-3xl font-bold text-gray-900">Side Projects & Interests</h3>
                </div>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Always building, always tinkering. Here are a few things keeping me busy lately:
                </p>
                
                <div className="flex flex-wrap gap-3 mb-10">
                  {['Mentoring PMs', 'Prototyping', 'Cooking', 'Hiking'].map(tag => (
                    <span key={tag} className="px-5 py-2.5 bg-white border border-[#FFEAEA] text-gray-700 rounded-full text-sm font-medium hover:border-[#FF5E7E] hover:text-[#FF5E7E] transition-all cursor-default shadow-sm">{tag}</span>
                  ))}
                </div>
                
                <div className="flex flex-col items-start gap-3">
                  <a 
                    href={SOCIAL_LINKS.sideProjects} 
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF7235] to-[#FF5E7E] text-white rounded-full text-sm font-bold hover:opacity-95 transition-all shadow-sm"
                  >
                    Sunny Meadow Petting Zoo (3D + Gemini TTS) <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                  <a 
                    href={SOCIAL_LINKS.nanoBanana} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-white border border-pink-100 text-pink-500 rounded-full text-sm font-bold hover:bg-pink-50 hover:border-pink-200 transition-all shadow-sm"
                  >
                    99 Things to Nano Banana About <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                  <a 
                    href={SOCIAL_LINKS.substack}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 px-6 py-3 bg-white/50 border border-gray-200 rounded-full text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-400 hover:bg-white transition-all w-fit"
                  >
                    Writing 'Thursday Thoughts' <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                </div>
              </div>
            </div>

            {/* Featured in Forbes Card */}
            <div className="relative group overflow-hidden rounded-[3rem] bg-gradient-to-br from-[#FFF0F3] to-white border border-[#FFEAEA] shadow-sm hover:shadow-xl transition-all duration-500">
              <div className="p-10 md:p-16 flex flex-col md:flex-row gap-12 items-center">
                <div className="flex-1 space-y-8">
                  <div className="inline-block px-4 py-1.5 bg-white border border-red-100 rounded-lg text-[10px] font-black uppercase tracking-widest text-red-500 shadow-sm">
                    Featured in Forbes
                  </div>
                  <h3 className="font-serif text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                    Raising AI-Native Kids: Creativity, Literacy, And Boundaries At Home
                  </h3>
                  <p className="text-lg text-gray-500 leading-relaxed max-w-xl">
                     I shared my perspective on how our family uses tools like Gemini and Opal to spark creativity and curiosity.
                  </p>
                  <a 
                    href="https://www.forbes.com/sites/yassprize/2025/10/02/raising-ai-native-kids-creativity-literacy-and-boundaries-at-home/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center gap-3 font-bold text-lg border-b-2 border-gray-900 pb-1 hover:text-gray-600 hover:border-gray-400 transition-all"
                  >
                    Read the full article <ArrowUpRight size={20} />
                  </a>
                </div>

                <div className="flex-1 w-full relative">
                  <div className="absolute top-0 left-0 -translate-x-8 -translate-y-4">
                    <Quote size={80} className="text-pink-200 opacity-50" />
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm p-10 md:p-12 rounded-[2rem] shadow-sm border border-white/50 relative z-10">
                     <p className="text-xl italic font-serif text-gray-700 leading-relaxed">
                      "It’s amazing to see how AI can make learning so delightful and personalized for my kids... I used the Gemini app to turn a line about birds flying out of a pie into a short video clip... I also used a tool called Opal to build a simple 'Fact Snacks' app..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

      </main>

      <Footer />
    </div>
  );
};

export default App;